import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen, Search, X, Plus, Trash2, Check, Loader2, Wand2 } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const BookRow = ({ book, action, busy, onAssign, onUnassign }) => (
  <div
    className="flex items-center gap-3 px-3 py-2 rounded-md border border-white/5 bg-white/[0.02] hover:bg-white/5 transition-colors"
    data-testid={`assign-book-row-${book.id}`}
  >
    <div className="w-10 h-14 flex-shrink-0 rounded overflow-hidden bg-gray-800/60 border border-white/5">
      {book.cover_image_url ? (
        <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <BookOpen className="h-4 w-4 text-gray-500" />
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-white font-medium truncate" title={book.title}>
        {book.title}
      </p>
      <p className="text-[11px] text-gray-400 truncate">by {book.author || 'Unknown'}</p>
      {book.auto_assigned && action === 'remove' && (
        <p className="text-[10px] text-emerald-300/70 mt-0.5 flex items-center gap-1">
          <Wand2 className="h-2.5 w-2.5" /> auto-matched
        </p>
      )}
    </div>
    {action === 'assign' ? (
      <Button
        size="sm"
        onClick={() => onAssign(book)}
        disabled={busy}
        className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
        data-testid={`assign-book-btn-${book.id}`}
      >
        {busy ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <>
            <Plus className="h-3 w-3 mr-1" /> Assign
          </>
        )}
      </Button>
    ) : (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onUnassign(book)}
        disabled={busy}
        className="h-7 px-2 text-xs text-red-300 hover:text-red-200 hover:bg-red-500/10"
        data-testid={`unassign-book-btn-${book.id}`}
      >
        {busy ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <>
            <Trash2 className="h-3 w-3 mr-1" /> Remove
          </>
        )}
      </Button>
    )}
  </div>
);

/**
 * AssignBooksModal — admin-side book ↔ author manual assignment.
 *
 * Solves the "registered name ≠ pen name" mismatch where auto-assign
 * never fired, so the author can't see their own book under My Books.
 *
 * Two columns:
 *   • Assigned (current My Books for this author)
 *   • Available (entire catalog filtered by search, minus the assigned set)
 *
 * Each row in Available has an "Assign" button → POST /admin/authors/{uid}/books
 * Each row in Assigned has a "Remove" button → DELETE /admin/authors/{uid}/books/{bid}
 *
 * Lists refresh in-place after every action. No page reload required.
 */
const AssignBooksModal = ({ user, onClose, onChanged }) => {
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [assigned, setAssigned] = useState([]);
  const [available, setAvailable] = useState([]);
  const [search, setSearch] = useState('');
  const { toast } = useToast();

  const [refreshTick, setRefreshTick] = useState(0);

  // Initial load + manual refresh on tick.
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/authors/${user.id}/books`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setAssigned(res.data?.assigned || []);
        setAvailable(res.data?.available || []);
      } catch (e) {
        if (cancelled) return;
        toast({
          title: 'Failed to load books',
          description: e?.response?.data?.detail || 'Try again.',
          variant: 'destructive',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, refreshTick, toast]);

  const triggerRefresh = () => setRefreshTick((t) => t + 1);

  const q = search.trim().toLowerCase();
  const filteredAvailable = q
    ? available.filter(
        (b) =>
          (b.title || '').toLowerCase().includes(q) ||
          (b.author || '').toLowerCase().includes(q),
      )
    : available;

  const assign = async (book) => {
    setBusyId(book.id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/authors/${user.id}/books`,
        { book_id: book.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: 'Book assigned',
        description: `"${book.title}" now appears in ${user.name}'s My Books.`,
      });
      triggerRefresh();
      onChanged && onChanged();
    } catch (e) {
      toast({
        title: 'Could not assign',
        description: e?.response?.data?.detail || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  const unassign = async (book) => {
    if (!window.confirm(`Remove "${book.title}" from ${user.name}'s My Books?`)) return;
    setBusyId(book.id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/authors/${user.id}/books/${book.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: 'Book removed',
        description: `"${book.title}" no longer appears in ${user.name}'s My Books.`,
      });
      triggerRefresh();
      onChanged && onChanged();
    } catch (e) {
      toast({
        title: 'Could not remove',
        description: e?.response?.data?.detail || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      data-testid="assign-books-modal"
    >
      <div className="bg-gray-900 rounded-xl w-full max-w-3xl max-h-[88vh] border border-white/10 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <BookOpen className="h-4 w-4 text-amber-300" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Manage author&apos;s books</h3>
              <p className="text-xs text-gray-400">
                {user.name} · <span className="text-gray-500">{user.email}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5"
            data-testid="assign-books-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
            {/* Assigned column */}
            <div className="bg-gray-900 flex flex-col min-h-0">
              <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-emerald-400" />
                  In My Books
                </p>
                <span
                  className="text-xs text-emerald-300 font-mono"
                  data-testid="assign-books-assigned-count"
                >
                  {assigned.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {assigned.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-12">
                    No books assigned yet. Pick from the right →
                  </p>
                ) : (
                  assigned.map((b) => (
                    <BookRow
                      key={b.id}
                      book={b}
                      action="remove"
                      busy={busyId === b.id}
                      onAssign={assign}
                      onUnassign={unassign}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Available column */}
            <div className="bg-gray-900 flex flex-col min-h-0">
              <div className="px-5 py-3 border-b border-white/5 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold flex items-center gap-2">
                    <BookOpen className="h-3.5 w-3.5 text-gray-400" />
                    Catalog (available)
                  </p>
                  <span
                    className="text-xs text-gray-400 font-mono"
                    data-testid="assign-books-available-count"
                  >
                    {filteredAvailable.length} / {available.length}
                  </span>
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by title or author"
                    className="bg-white/5 border-white/10 text-white text-sm h-8 pl-8"
                    data-testid="assign-books-search"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {filteredAvailable.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-12">
                    {search ? 'No matches in catalog.' : 'Every book is already assigned.'}
                  </p>
                ) : (
                  filteredAvailable.map((b) => (
                    <BookRow
                      key={b.id}
                      book={b}
                      action="assign"
                      busy={busyId === b.id}
                      onAssign={assign}
                      onUnassign={unassign}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-3 border-t border-white/10 flex items-center justify-between">
          <p className="text-[11px] text-gray-500">
            Changes take effect immediately — author will see updates on their next My Books refresh.
          </p>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-gray-300 hover:text-white"
            data-testid="assign-books-done"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignBooksModal;
